import FormV2PrintDocument from './form-v2-print-document.svelte'
import { createDefaultHeaderFooter } from '../types/document-header-footer'
import { isDocumentFrozen, readContextSnapshot, type FillContext } from './context-snapshot'
import type { FormV2Form } from '../../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'

/**
 * Contexto das tags {} para a impressão feita pela equipe.
 *
 * Documento congelado (>= 100) lê do contextSnapshot, exatamente como o link público e o PDF —
 * senão a impressão interna poderia divergir do PDF assinado sempre que um cadastro fosse editado
 * depois da assinatura, e essa divergência seria bem mais difícil de perceber que a do link público.
 * Abaixo de 100 o documento ainda é editável, então vale o dado atual.
 */
async function resolvePrintContext(form: FormV2Form): Promise<FillContext> {
    if (isDocumentFrozen(form.status)) {
        return readContextSnapshot((form.document as any)?.contextSnapshot)
    }

    let invoiceForPrint = null
    if (form.document?.invoiceId) {
        invoiceForPrint = await window.api.pos.invoices.getOne(form.document.invoiceId)
        // O getOne traz só o userId de cada item; sem resolver o nome, a tag {vendedor} sai '[-]'.
        if (invoiceForPrint?.services) {
            invoiceForPrint.services = await window.api.utils.utils.assignUsersFromObject(invoiceForPrint.services, false, 'userId', 'user', true)
        }
    }
    let billForPrint = null
    if (!invoiceForPrint && form.document?.billId) {
        billForPrint = await window.api.financials.bills.getOne(form.document.billId)
    }
    let personForPrint = null
    if (form.personId) {
        personForPrint = await window.api.events.persons.getOne(form.personId)
    }

    return {
        person: personForPrint,
        invoice: invoiceForPrint,
        bill: billForPrint,
        attendant: form.attendantName ? { userName: form.attendantName } : null,
        // `{usuario}` é quem criou o documento — só existe congelado, nunca vem da sessão de quem imprime.
        user: (form.document as any)?.contextSnapshot?.user ?? null,
    }
}

export async function printFormV2(formV2FormId: string): Promise<void> {
    try {
        const form: FormV2Form = await window.api.tools.formsV2.forms.getOne(formV2FormId)
        const snap = form.document?.templateSnapshot
        const context = await resolvePrintContext(form)

        const headerFooter = snap?.headerFooter ?? createDefaultHeaderFooter()

        window.printer({
            // FormV2PrintDocument = moldura (timbre repetido a cada página) + conteúdo. É o mesmo
            // componente usado na página pública que a lambda-pdf renderiza, pra que o documento
            // impresso aqui e o PDF que segue pra assinatura saiam idênticos.
            component: [FormV2PrintDocument],
            props: [
                {
                    blocks: snap?.blocks ?? [],
                    headerFooter,
                    answers: form.document?.answers ?? {},
                    context,
                    companyId: form.companyId,
                },
            ],
            waitLoadedEvent: true,
            // Cabeçalho/rodapé/marca d'água da empresa ficam de fora: o documento tem timbre próprio.
            hideHeader: true,
            hideFooter: true,
            hideWaterMark: true,
        })
    } catch (error) {
        window.error(error)
    }
}
